import { useState, useCallback } from 'react';
import { SeccionFormState, SECCION_FORM_INITIAL, SeccionPayload } from '../types/seccion.types';
import { Grado } from '../types/usuario.types';
import { seccionService } from '../services/seccionService';
import { catalogoService } from '../services/usuarioService';
import { catalogStorage } from '../utils/catalogStorage';

// ============================================================
// useSeccionForm – creación y edición de secciones
// ============================================================

export interface SeccionFormErrors { [field: string]: string }

export const useSeccionForm = (idSeccion: number | null, onSuccess: () => void) => {
  const isEditing = idSeccion !== null;

  const [form, setForm]               = useState<SeccionFormState>(SECCION_FORM_INITIAL);
  const [errors, setErrors]           = useState<SeccionFormErrors>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(false);
  const [grados, setGrados]           = useState<Grado[]>([]);

  const loadSeccion = useCallback(async () => {
    setLoadingInit(true);
    try {
      const grs = await catalogoService.getGrados();
      setGrados(grs);

      if (isEditing && idSeccion !== null) {
        const data = await seccionService.obtener(idSeccion);
        setForm({
          nombre:    data.nombre,
          gradoId:   data.gradoId ? String(data.gradoId) : '',
          capacidad: String(data.capacidad ?? 40),
          activo:    data.activo,
        });
      }
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idSeccion, isEditing]);

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const validate = (): SeccionFormErrors => {
    const e: SeccionFormErrors = {};
    if (!form.nombre.trim())                     e.nombre    = 'El nombre es requerido';
    if (!form.gradoId)                           e.gradoId   = 'El grado es requerido';
    const cap = Number(form.capacidad);
    if (!form.capacidad || cap < 1 || cap > 200) e.capacidad = 'Capacidad entre 1 y 200 alumnos';
    return e;
  };

  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: SeccionPayload = {
        nombre:    form.nombre.trim(),
        gradoId:   Number(form.gradoId),
        capacidad: Number(form.capacidad),
        activo:    form.activo,
      };
      if (isEditing && idSeccion) {
        await seccionService.modificar(idSeccion, payload);
      } else {
        await seccionService.crear(payload);
      }
      catalogStorage.clearSecciones();
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar la sección');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEditing, idSeccion]);

  return {
    form, errors, submitting, submitError, loadingInit, grados,
    handleChange, handleSubmit, loadSeccion,
  };
};
